import React from 'react';
import { useParams, Link, Navigate, useLocation } from 'react-router-dom';
import SEO from '../components/SEO';
import Breadcrumbs from '../components/Breadcrumbs';
import { LOCAL_SEO_PAGES } from '../data/localSeoPagesData';
import { COMPANY_INFO, CORE_SERVICES, MAIN_AREAS } from '../data/siteData';

export default function LocalSeoDetailPage({ forcedSlug }) {
  const { slug } = useParams();
  const location = useLocation();

  const currentSlug = forcedSlug || slug || location.pathname.replace(/^\//, '').replace(/\/$/, '');
  const pageData = LOCAL_SEO_PAGES[currentSlug];

  if (!pageData) {
    return <Navigate to="/omraader" replace />;
  }

  const currentArea = MAIN_AREAS.find((a) => a.slug === pageData.citySlug);
  const currentService = CORE_SERVICES.find((s) => s.slug === pageData.serviceSlug);

  return (
    <>
      <SEO
        title={pageData.seoTitle}
        description={pageData.metaDesc}
        ogImage={pageData.image}
        canonical={`https://xn--hrvejenstrpleje-xlbj.dk/${currentSlug}/`}
      />

      <Breadcrumbs
        items={[
          { label: 'Områder', href: '/omraader' },
          { label: pageData.city, href: `/${pageData.citySlug}` },
          { label: pageData.title },
        ]}
      />

      {/* Hero Header */}
      <section className="bg-[#10271c] text-white py-14 sm:py-20 px-4 sm:px-6 relative overflow-hidden">
        <div className="absolute inset-0 opacity-25">
          <img src={pageData.image} alt={pageData.title} className="w-full h-full object-cover" />
        </div>
        <div className="relative z-10 max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#1b4332] text-xs font-semibold text-[#8de0b4] mb-3">
            <i className="fa-solid fa-location-dot" aria-hidden="true" />
            <span>
              {pageData.city} • {pageData.service}
            </span>
          </div>
          <h1 className="text-3xl sm:text-5xl font-bold tracking-tight mb-4">
            {pageData.heading}
          </h1>
          <p className="text-base sm:text-lg text-[#cadcd1] leading-relaxed max-w-2xl mx-auto">
            {pageData.metaDesc}
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-14 sm:py-20 max-w-7xl mx-auto px-4 sm:px-6">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          {/* Main article */}
          <div className="lg:col-span-8 space-y-8">
            <div className="rounded-2xl overflow-hidden border border-[#dce5df] bg-white shadow-xs">
              <img
                src={pageData.image}
                alt={pageData.title}
                className="w-full max-h-[460px] object-cover"
              />
            </div>

            <div
              className="article-prose bg-white p-6 sm:p-10 rounded-2xl border border-[#dce5df] shadow-xs"
              dangerouslySetInnerHTML={{ __html: pageData.bodyHtml }}
            />

            {/* "Læs også:" in this city */}
            {pageData.sisterPages && pageData.sisterPages.length > 0 && (
              <div className="bg-[#f2f7f4] rounded-2xl p-6 sm:p-8 border border-[#cfe0d6]">
                <h3 className="text-xl font-bold text-[#14291d] mb-4 flex items-center gap-2">
                  <i className="fa-solid fa-tree-city text-[#2d6a4f]" aria-hidden="true" />
                  <span>Relateret træpleje i {pageData.city}:</span>
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {pageData.sisterPages.map((sister) => (
                    <Link
                      key={sister.slug}
                      to={`/${sister.slug}`}
                      className="p-4 rounded-xl bg-white border border-[#d4e1d9] hover:border-[#2d6a4f] shadow-2xs hover:shadow-xs transition-all flex flex-col group"
                    >
                      <h4 className="font-bold text-sm sm:text-base text-[#14291d] group-hover:text-[#2d6a4f] transition-colors mb-1">
                        {sister.title}
                      </h4>
                      <p className="text-xs text-[#526a5c] mb-3">{sister.desc}</p>
                      <span className="mt-auto text-xs font-semibold text-[#1b4332] flex items-center gap-1">
                        <span>Læs mere »</span>
                      </span>
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {/* Back link to Main City Page */}
            <div className="p-5 rounded-2xl bg-[#faf9f5] border border-[#e4ded0] flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <p className="text-xs font-semibold text-[#6b5847]">
                  Byprofil
                </p>
                <h4 className="font-bold text-base text-[#1c2c22]">
                  Læs mere om vores samlede arbejde i {pageData.city}
                </h4>
              </div>
              <Link
                to={`/${pageData.citySlug}`}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-[#1b4332] text-white text-xs font-semibold hover:bg-[#143627] transition-colors self-start sm:self-auto"
              >
                <span>Gå til {pageData.city} siden</span>
                <i className="fa-solid fa-arrow-right text-[10px]" aria-hidden="true" />
              </Link>
            </div>
          </div>

          {/* Sidebar */}
          <aside className="lg:col-span-4 space-y-6">
            {/* Direct phone / offer card */}
            <div className="bg-[#143627] text-white rounded-2xl p-6 shadow-sm">
              <span className="text-xs font-semibold text-[#7dd2a6]">
                Fast tilbud i {pageData.city}
              </span>
              <h3 className="text-xl font-bold mt-1 mb-2">Har du brug for hjælp?</h3>
              <p className="text-xs text-[#c8ded1] mb-5 leading-relaxed">
                Ring for en uforpligtende snak om dit træ i {pageData.city}. Vi giver altid en fast og ærlig pris.
              </p>
              <a
                href={`tel:${COMPANY_INFO.phoneRaw}`}
                className="w-full flex items-center justify-center gap-2 py-3 px-4 rounded-xl bg-white text-[#122b1f] font-bold text-sm hover:bg-[#f2f7f4] transition-colors whitespace-nowrap mb-2.5"
              >
                <i className="fa-solid fa-phone text-[#2d6a4f]" aria-hidden="true" />
                <span className="whitespace-nowrap">Ring {COMPANY_INFO.phone}</span>
              </a>
              <Link
                to="/kontakt"
                className="w-full flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl border border-white/30 text-white font-semibold text-xs hover:bg-white/10 transition-colors"
              >
                <span>Udfyld kontaktformular »</span>
              </Link>
            </div>

            {/* Core Service link */}
            {currentService && (
              <div className="bg-white rounded-2xl p-6 border border-[#dce5df] shadow-xs">
                <h4 className="text-sm font-bold text-[#14291d] mb-3">
                  Om kernetjenesten {currentService.shortTitle}
                </h4>
                <p className="text-xs text-[#4c5f53] leading-relaxed mb-4">
                  {currentService.intro}
                </p>
                <Link
                  to={`/${currentService.slug}`}
                  className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#1b4332] hover:underline"
                >
                  <span>Læs mere om {currentService.shortTitle} generelt »</span>
                </Link>
              </div>
            )}

            {/* Same service in other cities */}
            <div className="bg-white rounded-2xl p-6 border border-[#dce5df] shadow-xs">
              <h4 className="text-sm font-bold text-[#14291d] mb-3">
                {pageData.service} i andre byer
              </h4>
              <ul className="space-y-1.5 text-xs">
                {MAIN_AREAS.filter((a) => a.slug !== pageData.citySlug).map((otherArea) => {
                  const otherPage = otherArea.localPages.find(
                    (lp) => lp.service === pageData.service
                  );
                  if (!otherPage) return null;

                  return (
                    <li key={otherArea.slug}>
                      <Link
                        to={`/${otherPage.slug}`}
                        className="flex items-center justify-between p-2 rounded-lg hover:bg-[#f4f7f5] text-[#2c3d31]"
                      >
                        <span>{pageData.service} i {otherArea.name}</span>
                        <i className="fa-solid fa-chevron-right text-[10px] text-[#8ea396]" aria-hidden="true" />
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </div>
          </aside>
        </div>
      </section>
    </>
  );
}
